import type { YogaPose } from '../types'
import { defaultEvidence, defaultTiming, m, pose } from './helpers'

export const POSES: YogaPose[] = [
  pose({
    id: "mountain",
    english: "Mountain Pose",
    sanskrit: "Tadasana",
    difficulty: "Beginner",
    categories: ["Standing"],
    alignmentCues: ["Feet hip-width or together","Distribute weight evenly","Lengthen crown of head","Soft knees, engaged thighs"],
    commonMistakes: ["Locking knees","Anterior pelvic tilt / rib flaring","Holding breath"],
    precautions: ["Vertigo: gaze soft; avoid prolonged standing if contraindicated"],
    muscles: [m('quads', 'stabilizer'), m('glutes', 'stabilizer'), m('abs', 'stabilizer'), m('calves', 'stabilizer'), m('erectorSpinae', 'stabilizer')],
    evidence: defaultEvidence(
      "Upright axial alignment with minimal joint excursion.",
      "Isometric co-contraction maintains quiet standing balance.",
      [{ text: "Quiet stance involves continuous low-level ankle and hip postural strategies (Winter, 1995 overview).", url: "https://pubmed.ncbi.nlm.nih.gov/7586741/" }],
    ),
    keyframes: {
      "pose": {
        "mixamorigLeftArm": [
          34.1,
          0.7,
          -0.6
        ],
        "mixamorigLeftForeArm": [
          8.3,
          -0.4,
          -6.7
        ],
        "mixamorigLeftHand": [
          -9,
          -0.3,
          2.9
        ],
        "mixamorigRightArm": [
          34.1,
          -0.7,
          0.5
        ],
        "mixamorigRightForeArm": [
          8.3,
          0.4,
          6.8
        ],
        "mixamorigRightHand": [
          -9,
          0.4,
          -2
        ],
        "mixamorigLeftUpLeg": [
          -0.7,
          0,
          -1
        ],
        "mixamorigLeftLeg": [
          0.8,
          0,
          -2.7
        ],
        "mixamorigRightUpLeg": [
          -1.1,
          -0.1,
          1
        ],
        "mixamorigRightLeg": [
          1.3,
          0,
          2.7
        ]
      },
      "rootOffset": [
        0,
        0.008,
        0.001
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "warrior-ii",
    english: "Warrior II",
    sanskrit: "Virabhadrasana II",
    difficulty: "Beginner",
    categories: ["Standing"],
    alignmentCues: ["Front knee tracks over ankle","Back foot angled ~45–60°","Arms parallel to floor","Torso upright over pelvis"],
    commonMistakes: ["Front knee collapsing inward","Leaning over front leg","Shrugging shoulders"],
    precautions: ["Knee pain: shorten stance; avoid forcing depth"],
    muscles: [m('quads', 'working'), m('glutes', 'working'), m('abductors', 'stabilizer'), m('deltoids', 'stabilizer'), m('abs', 'stabilizer'), m('hipFlexors', 'lengthening')],
    evidence: defaultEvidence(
      "Front hip flexed/abducted; front knee flexed; arms abducted.",
      "Front-leg quads and glute med stabilize frontal-plane knee; arms hold isometric abduction.",
      [{ text: "Yoga asana EMG studies show pose-specific activation patterns (various; interpret cautiously).", url: "https://pubmed.ncbi.nlm.nih.gov/25419403/" }],
    ),
    keyframes: {
      "pose": {
        "mixamorigNeck": [
          0,
          35,
          0
        ],
        "mixamorigHead": [
          0,
          35,
          0
        ],
        "mixamorigLeftArm": [
          -50.2,
          1.8,
          90.5
        ],
        "mixamorigLeftForeArm": [
          7.2,
          0.1,
          -8.9
        ],
        "mixamorigLeftHand": [
          -9,
          -0.3,
          2.9
        ],
        "mixamorigRightArm": [
          -50.2,
          -1.7,
          89.5
        ],
        "mixamorigRightForeArm": [
          7.3,
          -0.1,
          9
        ],
        "mixamorigRightHand": [
          -9,
          0.4,
          -2
        ],
        "mixamorigLeftUpLeg": [
          71.8,
          4,
          -0.7
        ],
        "mixamorigLeftLeg": [
          -68.7,
          2.6,
          -6.6
        ],
        "mixamorigLeftFoot": [
          -3,
          -2.2,
          2.9
        ],
        "mixamorigRightUpLeg": [
          -58,
          79.1,
          7
        ],
        "mixamorigRightLeg": [
          1.3,
          0,
          2.7
        ],
        "mixamorigRightFoot": [
          1.5,
          38.5,
          32.7
        ]
      },
      "rootOffset": [
        0.027,
        -0.279,
        0.06
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "warrior-i",
    english: "Warrior I",
    sanskrit: "Virabhadrasana I",
    difficulty: "Beginner",
    categories: ["Standing","Backbends"],
    alignmentCues: ["Square pelvis toward front","Back heel grounded as able","Arms reach up","Front knee tracks toes"],
    commonMistakes: ["Over-arching lumbar","Front hip dumping forward","Forced back heel"],
    precautions: ["Shoulder issues: keep arms wider or hands on hips"],
    muscles: [m('quads', 'working'), m('glutes', 'working'), m('erectorSpinae', 'working'), m('deltoids', 'stabilizer'), m('hipFlexors', 'lengthening'), m('calves', 'lengthening')],
    evidence: defaultEvidence(
      "Asymmetric lunge with overhead reach and mild spinal extension.",
      "Front quads/glutes support flexed knee; posterior chain lengthens on rear leg.",
      [],
    ),
    keyframes: {
      "pose": {
        "mixamorigHips": [
          -5,
          0,
          0
        ],
        "mixamorigSpine": [
          -4,
          0,
          0
        ],
        "mixamorigSpine1": [
          -5,
          0,
          0
        ],
        "mixamorigSpine2": [
          -5,
          0,
          0
        ],
        "mixamorigNeck": [
          -8,
          0,
          0
        ],
        "mixamorigHead": [
          -6,
          0,
          0
        ],
        "mixamorigLeftShoulder": [
          -32.5,
          -2.3,
          7.8
        ],
        "mixamorigLeftArm": [
          80.3,
          -9.3,
          163.1
        ],
        "mixamorigLeftForeArm": [
          8.3,
          -0.5,
          -8.9
        ],
        "mixamorigLeftHand": [
          -9,
          -0.3,
          2.9
        ],
        "mixamorigRightShoulder": [
          -32.5,
          2.3,
          -7.9
        ],
        "mixamorigRightArm": [
          80.3,
          9.2,
          -163.2
        ],
        "mixamorigRightForeArm": [
          8.3,
          0.5,
          9
        ],
        "mixamorigRightHand": [
          -9,
          0.4,
          -2
        ],
        "mixamorigLeftUpLeg": [
          68,
          -2.4,
          0.9
        ],
        "mixamorigLeftLeg": [
          -75.8,
          2.6,
          -0.6
        ],
        "mixamorigLeftFoot": [
          2.7,
          0.1,
          -0.2
        ],
        "mixamorigRightUpLeg": [
          -57,
          50.9,
          -2.8
        ],
        "mixamorigRightLeg": [
          1.3,
          0,
          2.7
        ],
        "mixamorigRightFoot": [
          28.5,
          39.4,
          15.4
        ]
      },
      "rootOffset": [
        0.021,
        -0.288,
        0.092
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "warrior-iii",
    english: "Warrior III",
    sanskrit: "Virabhadrasana III",
    difficulty: "Intermediate",
    categories: ["Standing","Balance"],
    alignmentCues: ["Hips level","Standing leg soft micro-bend","Long line from heel to crown","Gaze steady"],
    commonMistakes: ["Hips rotating open","Hyperextended standing knee","Collapsing shoulders"],
    precautions: ["Balance disorders: practice near wall"],
    muscles: [m('glutes', 'working'), m('hamstrings', 'working'), m('erectorSpinae', 'stabilizer'), m('abs', 'stabilizer'), m('quads', 'stabilizer'), m('deltoids', 'stabilizer')],
    evidence: defaultEvidence(
      "Single-leg stance with torso and free leg near horizontal.",
      "Stance hip extensors and ankle strategies dominate balance.",
      [],
    ),
    keyframes: {
      "pose": {
        "mixamorigHips": [
          88,
          0,
          0
        ],
        "mixamorigNeck": [
          -12,
          0,
          0
        ],
        "mixamorigHead": [
          -8,
          0,
          0
        ],
        "mixamorigLeftShoulder": [
          -35.5,
          -0.9,
          2.9
        ],
        "mixamorigLeftArm": [
          81.1,
          -1.7,
          176.5
        ],
        "mixamorigLeftForeArm": [
          8.4,
          -0.1,
          -8.9
        ],
        "mixamorigLeftHand": [
          -9,
          -0.3,
          2.9
        ],
        "mixamorigRightShoulder": [
          -35.5,
          1,
          -3
        ],
        "mixamorigRightArm": [
          81.1,
          1.7,
          -176.6
        ],
        "mixamorigRightForeArm": [
          8.4,
          0.1,
          9
        ],
        "mixamorigRightHand": [
          -9,
          0.4,
          -2
        ],
        "mixamorigLeftUpLeg": [
          88.5,
          -2.1,
          -0.1
        ],
        "mixamorigLeftLeg": [
          -0.3,
          0.1,
          -2.7
        ],
        "mixamorigLeftFoot": [
          -0.1,
          -0.9,
          1.3
        ],
        "mixamorigRightUpLeg": [
          -4.3,
          -0.1,
          1
        ],
        "mixamorigRightLeg": [
          1.3,
          0,
          2.7
        ],
        "mixamorigRightFoot": [
          -158.3,
          -1.5,
          -178.7
        ]
      },
      "rootOffset": [
        0,
        -0.041,
        -0.035
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "triangle",
    english: "Triangle Pose",
    sanskrit: "Trikonasana",
    difficulty: "Beginner",
    categories: ["Standing","Twists"],
    alignmentCues: ["Long side body","Stack shoulders if accessible","Micro-bend in knees","Reach from hip crease"],
    commonMistakes: ["Rounding spine to reach floor","Locking knees","Forcing hand to floor"],
    precautions: ["Disc issues: reduce side-bend depth; keep length"],
    muscles: [m('abs', 'working'), m('abductors', 'stabilizer'), m('hamstrings', 'lengthening'), m('adductors', 'lengthening'), m('erectorSpinae', 'stabilizer')],
    evidence: defaultEvidence(
      "Wide stance with lateral flexion and free arm overhead.",
      "Obliques and QL eccentrically control side bend; hamstrings lengthen on front leg.",
      [],
    ),
    keyframes: {
      "pose": {
        "mixamorigHips": [
          40,
          -90,
          0
        ],
        "mixamorigSpine": [
          0,
          8,
          -14
        ],
        "mixamorigSpine1": [
          0,
          8,
          -14
        ],
        "mixamorigSpine2": [
          0,
          6,
          -10
        ],
        "mixamorigNeck": [
          0,
          -25,
          0
        ],
        "mixamorigHead": [
          0,
          -35,
          0
        ],
        "mixamorigLeftArm": [
          -41.5,
          13.2,
          20.2
        ],
        "mixamorigLeftForeArm": [
          -82.3,
          -81.1,
          -89.6
        ],
        "mixamorigLeftHand": [
          -9,
          -0.3,
          2.9
        ],
        "mixamorigRightArm": [
          -59.1,
          -13,
          14.6
        ],
        "mixamorigRightForeArm": [
          -82.4,
          81,
          89.6
        ],
        "mixamorigRightHand": [
          -9,
          0.4,
          -2
        ],
        "mixamorigLeftUpLeg": [
          71.7,
          -17.8,
          84.4
        ],
        "mixamorigLeftLeg": [
          0.8,
          0,
          -2.7
        ],
        "mixamorigLeftFoot": [
          -32.6,
          -16.9,
          7.2
        ],
        "mixamorigRightUpLeg": [
          0.3,
          -9.4,
          8.7
        ],
        "mixamorigRightLeg": [
          1.3,
          0,
          2.7
        ],
        "mixamorigRightFoot": [
          3.8,
          24.9,
          22.4
        ]
      },
      "rootOffset": [
        0.001,
        -0.08,
        -0.018
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "extended-side-angle",
    english: "Extended Side Angle",
    sanskrit: "Utthita Parsvakonasana",
    difficulty: "Intermediate",
    categories: ["Standing"],
    alignmentCues: ["Front knee over ankle","Long line from back heel to top hand","Bottom hand inside or on block"],
    commonMistakes: ["Collapsing into bottom shoulder","Front knee drifting in"],
    precautions: ["Shoulder injuries: top hand on hip"],
    muscles: [m('quads', 'working'), m('glutes', 'working'), m('abs', 'working'), m('adductors', 'lengthening'), m('deltoids', 'stabilizer')],
    evidence: defaultEvidence(
      "Lunged stance with lateral torso reach.",
      "Front-leg loading with oblique lengthening on top side.",
      [],
    ),
    keyframes: {
      "pose": {
        "mixamorigHips": [
          30,
          -90,
          0
        ],
        "mixamorigSpine": [
          0,
          6,
          -14
        ],
        "mixamorigSpine1": [
          0,
          6,
          -14
        ],
        "mixamorigSpine2": [
          0,
          6,
          -10
        ],
        "mixamorigNeck": [
          0,
          -20,
          0
        ],
        "mixamorigHead": [
          0,
          -30,
          0
        ],
        "mixamorigLeftArm": [
          -33.4,
          12.2,
          16.8
        ],
        "mixamorigLeftForeArm": [
          7.2,
          -0.2,
          -8.9
        ],
        "mixamorigLeftHand": [
          -9,
          -0.3,
          2.9
        ],
        "mixamorigRightArm": [
          -121.8,
          -15.1,
          -3.5
        ],
        "mixamorigRightForeArm": [
          -172.7,
          0.1,
          171
        ],
        "mixamorigRightHand": [
          -9,
          0.4,
          -2
        ],
        "mixamorigLeftUpLeg": [
          83.3,
          13.4,
          91.4
        ],
        "mixamorigLeftLeg": [
          -71.7,
          2.6,
          -6.4
        ],
        "mixamorigLeftFoot": [
          -3,
          -2,
          2.7
        ],
        "mixamorigRightUpLeg": [
          -4.4,
          -7,
          -25.4
        ],
        "mixamorigRightLeg": [
          1.3,
          0,
          2.7
        ],
        "mixamorigRightFoot": [
          -1.8,
          43.6,
          39.7
        ]
      },
      "rootOffset": [
        0.008,
        -0.263,
        0.079
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "forward-fold",
    english: "Standing Forward Fold",
    sanskrit: "Uttanasana",
    difficulty: "Beginner",
    categories: ["Standing","Forward bends"],
    alignmentCues: ["Hinge from hips","Soft knees as needed","Let head hang","Weight toward balls of feet"],
    commonMistakes: ["Rounding from lumbar to force hands down","Locked knees"],
    precautions: ["Disc herniation: bend knees deeply; avoid forceful flexion"],
    muscles: [m('hamstrings', 'lengthening'), m('erectorSpinae', 'lengthening'), m('calves', 'lengthening'), m('abs', 'stabilizer'), m('quads', 'stabilizer')],
    evidence: defaultEvidence(
      "Hip flexion with spinal flexion varying by individual.",
      "Hamstring and posterior chain lengthening; gravity assists.",
      [{ text: "Forward bends increase lumbar flexion; caution after disc injury (clinical consensus).", url: "https://pubmed.ncbi.nlm.nih.gov/20047983/" }],
    ),
    keyframes: {
      "pose": {
        "mixamorigHips": [
          80,
          0,
          0
        ],
        "mixamorigSpine": [
          14,
          0,
          0
        ],
        "mixamorigSpine1": [
          14,
          0,
          0
        ],
        "mixamorigSpine2": [
          10,
          0,
          0
        ],
        "mixamorigNeck": [
          12,
          0,
          0
        ],
        "mixamorigHead": [
          8,
          0,
          0
        ],
        "mixamorigLeftArm": [
          41.8,
          -3.6,
          116.3
        ],
        "mixamorigLeftForeArm": [
          8.9,
          -0.5,
          -3.2
        ],
        "mixamorigLeftHand": [
          -96.1,
          19.5,
          89.8
        ],
        "mixamorigRightArm": [
          41.8,
          3.6,
          -116.4
        ],
        "mixamorigRightForeArm": [
          8.9,
          0.5,
          3.3
        ],
        "mixamorigRightHand": [
          -96.1,
          -20.4,
          -89.7
        ],
        "mixamorigLeftUpLeg": [
          77,
          -2.1,
          1.2
        ],
        "mixamorigLeftLeg": [
          3.1,
          -0.1,
          -2.7
        ],
        "mixamorigRightUpLeg": [
          76.6,
          2.1,
          -1.2
        ],
        "mixamorigRightLeg": [
          3.5,
          0.1,
          2.7
        ]
      },
      "rootOffset": [
        0.001,
        -0.036,
        -0.201
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "chair",
    english: "Chair Pose",
    sanskrit: "Utkatasana",
    difficulty: "Beginner",
    categories: ["Standing"],
    alignmentCues: ["Sit hips back","Knees track toes","Chest lifted","Weight in heels"],
    commonMistakes: ["Knees past toes excessively","Lumbar overarch","Raised heels"],
    precautions: ["Patellofemoral pain: reduce depth"],
    muscles: [m('quads', 'working'), m('glutes', 'working'), m('soleus', 'working'), m('erectorSpinae', 'stabilizer'), m('deltoids', 'stabilizer')],
    evidence: defaultEvidence(
      "Bilateral knee/hip flexion with arms elevated.",
      "High quadriceps demand in isometric squat pattern.",
      [],
    ),
    keyframes: {
      "pose": {
        "mixamorigHips": [
          38,
          0,
          0
        ],
        "mixamorigSpine": [
          -6,
          0,
          0
        ],
        "mixamorigSpine1": [
          -6,
          0,
          0
        ],
        "mixamorigSpine2": [
          -4,
          0,
          0
        ],
        "mixamorigNeck": [
          -10,
          0,
          0
        ],
        "mixamorigHead": [
          -6,
          0,
          0
        ],
        "mixamorigLeftShoulder": [
          -33.1,
          1.3,
          -4.4
        ],
        "mixamorigLeftArm": [
          80.5,
          3.8,
          162
        ],
        "mixamorigLeftForeArm": [
          8.4,
          -0.3,
          -7.3
        ],
        "mixamorigLeftHand": [
          -9,
          -0.3,
          2.9
        ],
        "mixamorigRightShoulder": [
          -33.1,
          -1.3,
          4.4
        ],
        "mixamorigRightArm": [
          80.5,
          -3.8,
          -162.1
        ],
        "mixamorigRightForeArm": [
          8.4,
          0.3,
          7.4
        ],
        "mixamorigRightHand": [
          -9,
          0.4,
          -2
        ],
        "mixamorigLeftUpLeg": [
          97.4,
          -1.1,
          2.6
        ],
        "mixamorigLeftLeg": [
          -84.8,
          2.7,
          -0.2
        ],
        "mixamorigLeftFoot": [
          25.4,
          -1,
          -0.8
        ],
        "mixamorigRightUpLeg": [
          96.9,
          1.1,
          -2.5
        ],
        "mixamorigRightLeg": [
          -84.3,
          -2.7,
          0.2
        ],
        "mixamorigRightFoot": [
          26,
          1.1,
          0.7
        ]
      },
      "rootOffset": [
        0,
        -0.25,
        -0.196
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "goddess",
    english: "Goddess Pose",
    sanskrit: "Utkata Konasana",
    difficulty: "Beginner",
    categories: ["Standing"],
    alignmentCues: ["Wide stance, toes out","Knees track over toes","Torso upright","Pelvic floor optional cue — not a detox"],
    commonMistakes: ["Knees collapsing in","Tucking pelvis excessively"],
    precautions: ["Hip labral issues: reduce depth/width"],
    muscles: [m('quads', 'working'), m('glutes', 'working'), m('adductors', 'lengthening'), m('abs', 'stabilizer')],
    evidence: defaultEvidence(
      "Wide squat with external rotation.",
      "Quads/glutes load; adductors lengthen under abduction.",
      [],
    ),
    keyframes: {
      "pose": {
        "mixamorigLeftArm": [
          39.8,
          0.5,
          88.2
        ],
        "mixamorigLeftForeArm": [
          7.2,
          0.1,
          81.1
        ],
        "mixamorigLeftHand": [
          -9,
          -0.3,
          2.9
        ],
        "mixamorigRightArm": [
          -140.2,
          0.5,
          88.3
        ],
        "mixamorigRightForeArm": [
          7.3,
          -0.1,
          -81
        ],
        "mixamorigRightHand": [
          -9,
          0.4,
          -2
        ],
        "mixamorigLeftUpLeg": [
          75.4,
          10.3,
          -41.3
        ],
        "mixamorigLeftLeg": [
          -68.4,
          2.5,
          1
        ],
        "mixamorigLeftFoot": [
          -2.2,
          -1,
          -2.1
        ],
        "mixamorigRightUpLeg": [
          103,
          14.4,
          -139.7
        ],
        "mixamorigRightLeg": [
          -68,
          -2.6,
          -1
        ],
        "mixamorigRightFoot": [
          -1.5,
          1,
          2.1
        ]
      },
      "rootOffset": [
        0.14,
        -0.273,
        0.028
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "tree",
    english: "Tree Pose",
    sanskrit: "Vrksasana",
    difficulty: "Beginner",
    categories: ["Standing","Balance"],
    alignmentCues: ["Foot on ankle/calf/inner thigh — never on knee","Hips level","Soft gaze","Hands at heart or overhead"],
    commonMistakes: ["Pressing foot into knee joint","Hiking hip of standing leg"],
    precautions: ["Neuropathy / severe balance issues: use wall"],
    muscles: [m('glutes', 'working'), m('abductors', 'working'), m('quads', 'stabilizer'), m('abs', 'stabilizer'), m('calves', 'stabilizer')],
    evidence: defaultEvidence(
      "Single-leg stance with contralateral hip external rotation/abduction.",
      "Stance hip abductors and ankle invertors/evertors stabilize COM.",
      [{ text: "Yoga balance poses can improve postural control in older adults (meta-analyses vary).", url: "https://pubmed.ncbi.nlm.nih.gov/27151596/" }],
    ),
    keyframes: {
      "pose": {
        "mixamorigHips": [
          0,
          0,
          4
        ],
        "mixamorigLeftShoulder": [
          -30,
          -0.5,
          2
        ],
        "mixamorigLeftArm": [
          82.4,
          -0.9,
          178.4
        ],
        "mixamorigLeftForeArm": [
          12.8,
          -0.8,
          -8.9
        ],
        "mixamorigLeftHand": [
          -9,
          -0.3,
          2.9
        ],
        "mixamorigRightShoulder": [
          -38,
          0.8,
          -2.5
        ],
        "mixamorigRightArm": [
          82.4,
          0.8,
          -178.5
        ],
        "mixamorigRightForeArm": [
          12.9,
          0.8,
          9
        ],
        "mixamorigRightHand": [
          -9,
          0.4,
          -2
        ],
        "mixamorigLeftUpLeg": [
          -0.7,
          0,
          -5
        ],
        "mixamorigLeftLeg": [
          0.8,
          0,
          -2.7
        ],
        "mixamorigLeftFoot": [
          -0.1,
          -1.8,
          0.7
        ],
        "mixamorigRightUpLeg": [
          91.4,
          18,
          -81.5
        ],
        "mixamorigRightLeg": [
          -140.6,
          -1.7,
          -2.1
        ],
        "mixamorigRightFoot": [
          8.1,
          9.3,
          -5.7
        ]
      },
      "rootOffset": [
        0.146,
        0.002,
        0.001
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "eagle",
    english: "Eagle Pose",
    sanskrit: "Garudasana",
    difficulty: "Intermediate",
    categories: ["Balance","Standing"],
    alignmentCues: ["Wrap elbows and thighs as accessible","Sit hips back","Lift elbows"],
    commonMistakes: ["Forcing wraps","Collapsing chest"],
    precautions: ["Knee/shoulder replacements: skip wraps"],
    muscles: [m('quads', 'working'), m('glutes', 'working'), m('abductors', 'lengthening'), m('deltoids', 'stabilizer')],
    evidence: defaultEvidence(
      "Crossed limbs with flexed stance.",
      "Balance demand plus isometric squat load.",
      [],
    ),
    keyframes: {
      "pose": {
        "mixamorigHips": [
          25,
          0,
          0
        ],
        "mixamorigSpine": [
          -5,
          0,
          0
        ],
        "mixamorigSpine1": [
          -5,
          0,
          0
        ],
        "mixamorigSpine2": [
          -3,
          0,
          0
        ],
        "mixamorigLeftArm": [
          32.7,
          23.8,
          96.5
        ],
        "mixamorigLeftForeArm": [
          -16,
          4.7,
          79.3
        ],
        "mixamorigLeftHand": [
          -9,
          -0.3,
          2.9
        ],
        "mixamorigRightArm": [
          31.5,
          -23.5,
          -99.9
        ],
        "mixamorigRightForeArm": [
          -15.5,
          -6,
          -76.1
        ],
        "mixamorigRightHand": [
          -9,
          0.4,
          -2
        ],
        "mixamorigLeftUpLeg": [
          61.2,
          -1.9,
          -1
        ],
        "mixamorigLeftLeg": [
          -61.6,
          2.4,
          -1.3
        ],
        "mixamorigLeftFoot": [
          25.4,
          -0.2,
          1.6
        ],
        "mixamorigRightUpLeg": [
          77.5,
          -1.4,
          27.3
        ],
        "mixamorigRightLeg": [
          -96.5,
          -2.7,
          -0.4
        ],
        "mixamorigRightFoot": [
          154.7,
          -26.1,
          -178.1
        ]
      },
      "rootOffset": [
        0,
        -0.119,
        -0.109
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "dancer",
    english: "Dancer Pose",
    sanskrit: "Natarajasana",
    difficulty: "Advanced",
    categories: ["Balance","Backbends"],
    alignmentCues: ["Kick into hand","Keep hips level","Lift chest","Soft standing knee"],
    commonMistakes: ["Hinging only at lumbar","Opening hip out"],
    precautions: ["Low back pain: reduce kick height"],
    muscles: [m('glutes', 'working'), m('hamstrings', 'working'), m('quads', 'lengthening'), m('erectorSpinae', 'working'), m('abs', 'stabilizer')],
    evidence: defaultEvidence(
      "Standing backbend with grasped rear foot.",
      "Stance balance + hip extension and spinal extension.",
      [],
    ),
    keyframes: {
      "pose": {
        "mixamorigHips": [
          35,
          0,
          0
        ],
        "mixamorigSpine": [
          -10,
          0,
          0
        ],
        "mixamorigSpine1": [
          -10,
          0,
          0
        ],
        "mixamorigSpine2": [
          -6,
          0,
          0
        ],
        "mixamorigNeck": [
          -10,
          0,
          0
        ],
        "mixamorigLeftArm": [
          43.6,
          -3.9,
          130.9
        ],
        "mixamorigLeftForeArm": [
          7.2,
          0.1,
          -8.9
        ],
        "mixamorigLeftHand": [
          -9,
          -0.3,
          2.9
        ],
        "mixamorigRightArm": [
          38.9,
          -7.5,
          71.1
        ],
        "mixamorigRightForeArm": [
          11.7,
          0.5,
          41.2
        ],
        "mixamorigRightHand": [
          -9,
          0.4,
          -2
        ],
        "mixamorigLeftUpLeg": [
          34.3,
          -1.2,
          -1.8
        ],
        "mixamorigLeftLeg": [
          0.8,
          0,
          -2.7
        ],
        "mixamorigLeftFoot": [
          -0.1,
          -0.9,
          1.3
        ],
        "mixamorigRightUpLeg": [
          -70.6,
          -2.1,
          -2.2
        ],
        "mixamorigRightLeg": [
          -91.8,
          -2.7,
          -0.1
        ]
      },
      "rootOffset": [
        -0.001,
        -0.002,
        -0.046
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "staff",
    english: "Staff Pose",
    sanskrit: "Dandasana",
    difficulty: "Beginner",
    categories: ["Seated"],
    alignmentCues: ["Sit on sit bones","Legs active","Spine long","Shoulders over hips"],
    commonMistakes: ["Rounding lower back","Collapsed chest"],
    precautions: ["Tight hamstrings: sit on folded blanket"],
    muscles: [m('abs', 'stabilizer'), m('erectorSpinae', 'stabilizer'), m('quads', 'stabilizer'), m('hipFlexors', 'stabilizer')],
    evidence: defaultEvidence(
      "Seated with hips flexed ~90°, knees extended.",
      "Isometric trunk and hip flexor activity to hold upright seat.",
      [],
    ),
    keyframes: {
      "pose": {
        "mixamorigHips": [
          -4,
          0,
          0
        ],
        "mixamorigLeftArm": [
          31.3,
          1.3,
          -10.2
        ],
        "mixamorigLeftForeArm": [
          -81.9,
          -85.6,
          -93.1
        ],
        "mixamorigLeftHand": [
          -104.7,
          1.7,
          179.8
        ],
        "mixamorigRightArm": [
          31.3,
          -1.3,
          10.1
        ],
        "mixamorigRightForeArm": [
          -82,
          85.5,
          93.2
        ],
        "mixamorigRightHand": [
          -104.7,
          -2.6,
          -179.9
        ],
        "mixamorigLeftUpLeg": [
          85.3,
          -2.1,
          1
        ],
        "mixamorigLeftLeg": [
          0.8,
          0,
          -2.7
        ],
        "mixamorigRightUpLeg": [
          84.9,
          2.1,
          -0.9
        ],
        "mixamorigRightLeg": [
          1.3,
          0,
          2.7
        ]
      },
      "rootOffset": [
        0.001,
        -0.787,
        -0.356
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "seated-forward-fold",
    english: "Seated Forward Fold",
    sanskrit: "Paschimottanasana",
    difficulty: "Beginner",
    categories: ["Seated","Forward bends"],
    alignmentCues: ["Hinge from hips","Long spine then deepen","Knees soft as needed"],
    commonMistakes: ["Pulling with arms aggressively","Rounding to touch toes"],
    precautions: ["Sciatica/disc: prioritize length over depth"],
    muscles: [m('hamstrings', 'lengthening'), m('erectorSpinae', 'lengthening'), m('calves', 'lengthening'), m('abs', 'working')],
    evidence: defaultEvidence(
      "Seated hip flexion with spinal flexion.",
      "Posterior chain lengthening under controlled flexion.",
      [],
    ),
    keyframes: {
      "pose": {
        "mixamorigHips": [
          50,
          0,
          0
        ],
        "mixamorigSpine": [
          14,
          0,
          0
        ],
        "mixamorigSpine1": [
          14,
          0,
          0
        ],
        "mixamorigSpine2": [
          8,
          0,
          0
        ],
        "mixamorigNeck": [
          8,
          0,
          0
        ],
        "mixamorigHead": [
          6,
          0,
          0
        ],
        "mixamorigLeftArm": [
          47,
          -0.2,
          156.8
        ],
        "mixamorigLeftForeArm": [
          11,
          1.5,
          -6
        ],
        "mixamorigLeftHand": [
          -9,
          -0.3,
          2.9
        ],
        "mixamorigRightArm": [
          47,
          0.2,
          -156.9
        ],
        "mixamorigRightForeArm": [
          11,
          -1.5,
          6.1
        ],
        "mixamorigRightHand": [
          -9,
          0.4,
          -2
        ],
        "mixamorigLeftUpLeg": [
          139.3,
          -1.4,
          2.8
        ],
        "mixamorigLeftLeg": [
          0.8,
          0,
          -2.7
        ],
        "mixamorigRightUpLeg": [
          138.9,
          1.4,
          -2.7
        ],
        "mixamorigRightLeg": [
          1.3,
          0,
          2.7
        ]
      },
      "rootOffset": [
        0,
        -0.808,
        -0.397
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "bound-angle",
    english: "Bound Angle Pose",
    sanskrit: "Baddha Konasana",
    difficulty: "Beginner",
    categories: ["Seated"],
    alignmentCues: ["Soles together","Knees fall open","Long spine","Optional fold"],
    commonMistakes: ["Forcing knees down","Rounding spine"],
    precautions: ["Hip/knee replacements: limit range"],
    muscles: [m('adductors', 'lengthening'), m('piriformis', 'lengthening'), m('abs', 'stabilizer'), m('erectorSpinae', 'stabilizer')],
    evidence: defaultEvidence(
      "Hip flexion, abduction, external rotation.",
      "Adductor lengthening; no evidence for “detox” — liver/kidneys handle metabolic waste.",
      [],
    ),
    keyframes: {
      "pose": {
        "mixamorigHips": [
          8,
          0,
          0
        ],
        "mixamorigSpine": [
          -3,
          0,
          0
        ],
        "mixamorigLeftArm": [
          -3.3,
          44.7,
          46.7
        ],
        "mixamorigLeftForeArm": [
          16.7,
          -57.5,
          11.1
        ],
        "mixamorigLeftHand": [
          -9,
          -0.3,
          2.9
        ],
        "mixamorigRightArm": [
          -3.4,
          -44.6,
          -46.9
        ],
        "mixamorigRightForeArm": [
          16.5,
          57.6,
          -10.9
        ],
        "mixamorigRightHand": [
          -9,
          0.4,
          -2
        ],
        "mixamorigLeftUpLeg": [
          167,
          -37,
          92.6
        ],
        "mixamorigLeftLeg": [
          -123.5,
          2.2,
          1.5
        ],
        "mixamorigLeftFoot": [
          7.7,
          11.5,
          -6.4
        ],
        "mixamorigRightUpLeg": [
          166.6,
          37,
          -92.6
        ],
        "mixamorigRightLeg": [
          -123.1,
          -2.3,
          -1.5
        ],
        "mixamorigRightFoot": [
          8.4,
          -11.4,
          6.4
        ]
      },
      "rootOffset": [
        0,
        -0.668,
        -0.187
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "cow-face",
    english: "Cow Face Pose",
    sanskrit: "Gomukhasana",
    difficulty: "Intermediate",
    categories: ["Seated"],
    alignmentCues: ["Stack knees as able","Sit evenly","Arm bind optional"],
    commonMistakes: ["Forcing shoulder bind","Leaning to one sit bone"],
    precautions: ["Rotator cuff issues: skip arm bind"],
    muscles: [m('piriformis', 'lengthening'), m('glutes', 'lengthening'), m('triceps', 'lengthening'), m('pecs', 'lengthening'), m('abs', 'stabilizer')],
    evidence: defaultEvidence(
      "Crossed thighs with optional opposing shoulder rotation.",
      "External hip rotators and shoulder capsule mobility demand.",
      [],
    ),
    keyframes: {
      "pose": {
        "mixamorigLeftArm": [
          -173.7,
          26.8,
          -140.2
        ],
        "mixamorigLeftForeArm": [
          -49.9,
          -28.4,
          107.2
        ],
        "mixamorigLeftHand": [
          -9,
          -0.3,
          2.9
        ],
        "mixamorigRightShoulder": [
          -33,
          0.6,
          -2.2
        ],
        "mixamorigRightArm": [
          89.5,
          6.3,
          169.4
        ],
        "mixamorigRightForeArm": [
          89.3,
          -42,
          -95.3
        ],
        "mixamorigRightHand": [
          -9,
          0.4,
          -2
        ],
        "mixamorigLeftUpLeg": [
          31.9,
          -71.2,
          -52.3
        ],
        "mixamorigLeftLeg": [
          -129.8,
          2.1,
          1.8
        ],
        "mixamorigLeftFoot": [
          -36.1,
          -73.3,
          41.6
        ],
        "mixamorigRightUpLeg": [
          42.5,
          73.9,
          55.1
        ],
        "mixamorigRightLeg": [
          -129.5,
          -2.1,
          -1.8
        ],
        "mixamorigRightFoot": [
          -90.5,
          82.3,
          10.7
        ]
      },
      "rootOffset": [
        0,
        -0.736,
        -0.059
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "seated-twist",
    english: "Seated Spinal Twist",
    sanskrit: "Ardha Matsyendrasana",
    difficulty: "Intermediate",
    categories: ["Seated","Twists"],
    alignmentCues: ["Lengthen before rotating","Initiate from thoracic","Keep both sit bones down"],
    commonMistakes: ["Yank with arm","Collapsing height"],
    precautions: ["Acute disc / pregnancy modifications as advised by clinician"],
    muscles: [m('abs', 'working'), m('erectorSpinae', 'working'), m('glutes', 'stabilizer'), m('piriformis', 'lengthening')],
    evidence: defaultEvidence(
      "Axial rotation of trunk over flexed hips.",
      "Obliques and rotatores contribute; twists do not “wring toxins” from organs — hepatic/renal clearance does.",
      [{ text: "Liver and kidneys metabolize and excrete waste; yoga twists are not a detox mechanism.", url: "https://www.ncbi.nlm.nih.gov/books/NBK541075/" }],
    ),
    keyframes: {
      "pose": {
        "mixamorigSpine": [
          0,
          14,
          0
        ],
        "mixamorigSpine1": [
          0,
          14,
          0
        ],
        "mixamorigSpine2": [
          0,
          14,
          0
        ],
        "mixamorigNeck": [
          0,
          18,
          0
        ],
        "mixamorigHead": [
          0,
          18,
          0
        ],
        "mixamorigLeftArm": [
          8.7,
          39,
          -7.8
        ],
        "mixamorigLeftForeArm": [
          113,
          84.1,
          -102.1
        ],
        "mixamorigLeftHand": [
          -128.3,
          -21.8,
          -172.4
        ],
        "mixamorigRightArm": [
          38.5,
          6.1,
          -77.2
        ],
        "mixamorigRightForeArm": [
          -27.1,
          -4,
          -84.3
        ],
        "mixamorigRightHand": [
          -9,
          0.4,
          -2
        ],
        "mixamorigLeftUpLeg": [
          116.7,
          -54.3,
          -12.7
        ],
        "mixamorigLeftLeg": [
          -129.6,
          2.1,
          1.8
        ],
        "mixamorigLeftFoot": [
          2,
          8.8,
          54.5
        ],
        "mixamorigRightUpLeg": [
          31.5,
          71.2,
          52.4
        ],
        "mixamorigRightLeg": [
          -129.4,
          -2.1,
          -1.8
        ],
        "mixamorigRightFoot": [
          -35.4,
          73.3,
          -41.6
        ]
      },
      "rootOffset": [
        -0.068,
        -0.73,
        0.057
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "bridge",
    english: "Bridge Pose",
    sanskrit: "Setu Bandha Sarvangasana",
    difficulty: "Beginner",
    categories: ["Supine","Backbends"],
    alignmentCues: ["Feet under knees","Press into feet to lift hips","Optional clasp hands","Soft throat"],
    commonMistakes: ["Knees splaying","Pushing into cervical spine"],
    precautions: ["Neck injuries: keep lift modest; avoid plow variations"],
    muscles: [m('glutes', 'working'), m('hamstrings', 'working'), m('erectorSpinae', 'working'), m('quads', 'lengthening'), m('abs', 'lengthening')],
    evidence: defaultEvidence(
      "Supine hip extension / mild spinal extension.",
      "Glute-ham dominant hip extension; cervical should not bear load.",
      [],
    ),
    keyframes: {
      "pose": {
        "mixamorigHips": [
          -123.5,
          0,
          0
        ],
        "mixamorigNeck": [
          22.3,
          0,
          0
        ],
        "mixamorigHead": [
          14.1,
          0,
          0
        ],
        "mixamorigLeftArm": [
          34.2,
          4.4,
          -41.9
        ],
        "mixamorigLeftForeArm": [
          99.3,
          85,
          -89.8
        ],
        "mixamorigLeftHand": [
          -9,
          -0.3,
          2.9
        ],
        "mixamorigRightArm": [
          34.2,
          -4.4,
          41.8
        ],
        "mixamorigRightForeArm": [
          99.2,
          -84.9,
          89.7
        ],
        "mixamorigRightHand": [
          -9,
          0.4,
          -2
        ],
        "mixamorigLeftUpLeg": [
          -21.4,
          1.5,
          0.3
        ],
        "mixamorigLeftLeg": [
          -106.5,
          2.6,
          0.8
        ],
        "mixamorigLeftFoot": [
          4.4,
          0.4,
          -0.7
        ],
        "mixamorigRightUpLeg": [
          -21.9,
          -1.5,
          -0.3
        ],
        "mixamorigRightLeg": [
          -106.1,
          -2.6,
          -0.8
        ],
        "mixamorigRightFoot": [
          5.1,
          -0.3,
          0.7
        ]
      },
      "rootOffset": [
        0.001,
        -0.567,
        0.04
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "supine-twist",
    english: "Supine Twist",
    sanskrit: "Supta Matsyendrasana",
    difficulty: "Beginner",
    categories: ["Supine","Twists"],
    alignmentCues: ["Both shoulders grounded as able","Bend knees","Gentle rotation"],
    commonMistakes: ["Forcing knee to floor","Lifting opposite shoulder"],
    precautions: ["SI joint irritability: reduce range"],
    muscles: [m('abs', 'lengthening'), m('glutes', 'lengthening'), m('erectorSpinae', 'lengthening'), m('pecs', 'lengthening')],
    evidence: defaultEvidence(
      "Spinal rotation with hips flexed.",
      "Passive stretch of obliques/ITB region; not organ detox.",
      [],
    ),
    keyframes: {
      "pose": {
        "mixamorigHips": [
          -90,
          44.6,
          0
        ],
        "mixamorigSpine1": [
          3.7,
          -24.2,
          0
        ],
        "mixamorigSpine2": [
          0,
          -20.4,
          0
        ],
        "mixamorigNeck": [
          -3.7,
          -20.4,
          0
        ],
        "mixamorigHead": [
          0,
          -28.2,
          0
        ],
        "mixamorigLeftArm": [
          -50.2,
          -2,
          -2.4
        ],
        "mixamorigLeftForeArm": [
          -82.3,
          -81.1,
          -89.6
        ],
        "mixamorigLeftHand": [
          -9,
          -0.3,
          2.9
        ],
        "mixamorigRightArm": [
          -50.2,
          2.1,
          2.4
        ],
        "mixamorigRightForeArm": [
          -82.4,
          81,
          89.6
        ],
        "mixamorigRightHand": [
          -9,
          0.4,
          -2
        ],
        "mixamorigLeftUpLeg": [
          94.8,
          20.6,
          17.9
        ],
        "mixamorigLeftLeg": [
          -101.3,
          2.7,
          0.6
        ],
        "mixamorigRightUpLeg": [
          94.5,
          24.8,
          17.5
        ],
        "mixamorigRightLeg": [
          -100.9,
          -2.7,
          -0.6
        ]
      },
      "rootOffset": [
        0.009,
        -0.794,
        0.159
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "happy-baby",
    english: "Happy Baby",
    sanskrit: "Ananda Balasana",
    difficulty: "Beginner",
    categories: ["Supine"],
    alignmentCues: ["Hold outer feet or calves","Knees toward armpits","Sacrum heavy"],
    commonMistakes: ["Yank feet; lift head"],
    precautions: ["Knee pain: hold behind thighs"],
    muscles: [m('hamstrings', 'lengthening'), m('adductors', 'lengthening'), m('glutes', 'lengthening'), m('abs', 'stabilizer')],
    evidence: defaultEvidence(
      "Hip flexion with knees flexed, often some abduction.",
      "Posterior chain and adductors lengthen passively.",
      [],
    ),
    keyframes: {
      "pose": {
        "mixamorigHips": [
          -90,
          0,
          0
        ],
        "mixamorigLeftArm": [
          38.8,
          -28.9,
          67.1
        ],
        "mixamorigLeftForeArm": [
          16,
          10.6,
          -11.9
        ],
        "mixamorigLeftHand": [
          -9,
          -0.3,
          2.9
        ],
        "mixamorigRightArm": [
          38.9,
          28.8,
          -67.2
        ],
        "mixamorigRightForeArm": [
          16,
          -10.6,
          12
        ],
        "mixamorigRightHand": [
          -9,
          0.4,
          -2
        ],
        "mixamorigLeftUpLeg": [
          160.5,
          -19.6,
          54
        ],
        "mixamorigLeftLeg": [
          -58.6,
          2.3,
          -1.4
        ],
        "mixamorigRightUpLeg": [
          160,
          19.7,
          -54
        ],
        "mixamorigRightLeg": [
          -58.2,
          -2.4,
          1.4
        ]
      },
      "rootOffset": [
        -0.001,
        -0.814,
        0.315
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "cobra",
    english: "Cobra Pose",
    sanskrit: "Bhujangasana",
    difficulty: "Beginner",
    categories: ["Prone","Backbends"],
    alignmentCues: ["Pubis / pelvis grounded","Elbows soft bend","Lift chest with back body","Shoulders away from ears"],
    commonMistakes: ["Pushing only with arms","Compressing low back","Raising shoulders"],
    precautions: ["Acute lumbar pain: keep low; try sphinx"],
    muscles: [m('erectorSpinae', 'working'), m('glutes', 'stabilizer'), m('abs', 'lengthening'), m('pecs', 'lengthening'), m('traps', 'stabilizer')],
    evidence: defaultEvidence(
      "Prone spinal extension with hands assisting.",
      "Erector spinae concentric; abdominal wall lengthens.",
      [{ text: "McKenzie-style extension is used clinically for some back presentations — individualize.", url: "https://pubmed.ncbi.nlm.nih.gov/16985470/" }],
    ),
    keyframes: {
      "pose": {
        "mixamorigHips": [
          90,
          0,
          0
        ],
        "mixamorigSpine": [
          -18,
          0,
          0
        ],
        "mixamorigSpine1": [
          -20,
          0,
          0
        ],
        "mixamorigSpine2": [
          -18,
          0,
          0
        ],
        "mixamorigNeck": [
          -14,
          0,
          0
        ],
        "mixamorigHead": [
          -16,
          0,
          0
        ],
        "mixamorigLeftArm": [
          28.1,
          3.7,
          2.8
        ],
        "mixamorigLeftForeArm": [
          110.5,
          -37.1,
          98
        ],
        "mixamorigLeftHand": [
          -133.2,
          0.5,
          178.2
        ],
        "mixamorigRightArm": [
          28.1,
          -3.7,
          -2.9
        ],
        "mixamorigRightForeArm": [
          110.5,
          37.3,
          -98.1
        ],
        "mixamorigRightHand": [
          -133.3,
          -1.2,
          -178.7
        ],
        "mixamorigLeftUpLeg": [
          0.5,
          0,
          0.7
        ],
        "mixamorigLeftLeg": [
          0.8,
          0,
          -3.3
        ],
        "mixamorigLeftFoot": [
          -43.3,
          -2.1,
          -1.9
        ],
        "mixamorigRightUpLeg": [
          0,
          -0.1,
          -0.8
        ],
        "mixamorigRightLeg": [
          1.3,
          0,
          3.3
        ],
        "mixamorigRightFoot": [
          -42.6,
          2.2,
          1.9
        ]
      },
      "rootOffset": [
        0.001,
        -0.724,
        0.283
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "sphinx",
    english: "Sphinx Pose",
    sanskrit: "Salamba Bhujangasana",
    difficulty: "Beginner",
    categories: ["Prone","Backbends"],
    alignmentCues: ["Forearms on mat","Elbows under shoulders","Lift through sternum","Legs active"],
    commonMistakes: ["Collapsing into lumbar","Shrugging"],
    precautions: ["Wrist-friendly alternative to cobra"],
    muscles: [m('erectorSpinae', 'working'), m('traps', 'stabilizer'), m('abs', 'lengthening'), m('glutes', 'stabilizer')],
    evidence: defaultEvidence(
      "Low prone extension on forearms.",
      "Mild erector demand; good for gentle extension tolerance.",
      [],
    ),
    keyframes: {
      "pose": {
        "mixamorigHips": [
          90,
          0,
          0
        ],
        "mixamorigSpine": [
          -8,
          0,
          0
        ],
        "mixamorigSpine1": [
          -10,
          0,
          0
        ],
        "mixamorigSpine2": [
          -10,
          0,
          0
        ],
        "mixamorigNeck": [
          -5,
          0,
          0
        ],
        "mixamorigHead": [
          -8,
          0,
          0
        ],
        "mixamorigLeftArm": [
          37.5,
          -3.5,
          63
        ],
        "mixamorigLeftForeArm": [
          -78.2,
          13.5,
          92
        ],
        "mixamorigLeftHand": [
          -9,
          -0.3,
          2.9
        ],
        "mixamorigRightArm": [
          37.5,
          3.5,
          -63.1
        ],
        "mixamorigRightForeArm": [
          -78.1,
          -13.6,
          -92
        ],
        "mixamorigRightHand": [
          -9,
          0.4,
          -2
        ],
        "mixamorigLeftUpLeg": [
          0.5,
          0,
          0.7
        ],
        "mixamorigLeftLeg": [
          0.8,
          0,
          -3.3
        ],
        "mixamorigLeftFoot": [
          -43.3,
          -2.1,
          -1.9
        ],
        "mixamorigRightUpLeg": [
          0,
          -0.1,
          -0.8
        ],
        "mixamorigRightLeg": [
          1.3,
          0,
          3.3
        ],
        "mixamorigRightFoot": [
          -42.6,
          2.2,
          1.9
        ]
      },
      "rootOffset": [
        0,
        -0.798,
        0.161
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "locust",
    english: "Locust Pose",
    sanskrit: "Salabhasana",
    difficulty: "Intermediate",
    categories: ["Prone","Backbends"],
    alignmentCues: ["Lift chest and legs","Reach back through toes","Broad collarbones"],
    commonMistakes: ["Over-compressing lumbar","Bent elbows winging"],
    precautions: ["Pregnancy / recent abdominal surgery: skip"],
    muscles: [m('erectorSpinae', 'working'), m('glutes', 'working'), m('hamstrings', 'working'), m('deltoids', 'stabilizer'), m('abs', 'lengthening')],
    evidence: defaultEvidence(
      "Prone lift of torso and lower limbs.",
      "Posterior chain concentric work.",
      [],
    ),
    keyframes: {
      "pose": {
        "mixamorigHips": [
          90,
          0,
          0
        ],
        "mixamorigSpine": [
          -8,
          0,
          0
        ],
        "mixamorigSpine1": [
          -8,
          0,
          0
        ],
        "mixamorigSpine2": [
          -8,
          0,
          0
        ],
        "mixamorigNeck": [
          -4,
          0,
          0
        ],
        "mixamorigLeftArm": [
          32.6,
          3.9,
          -42.1
        ],
        "mixamorigLeftForeArm": [
          -80.3,
          -81,
          -88.6
        ],
        "mixamorigLeftHand": [
          -9,
          -0.3,
          2.9
        ],
        "mixamorigRightArm": [
          32.6,
          -3.9,
          41.9
        ],
        "mixamorigRightForeArm": [
          -80.3,
          80.9,
          88.7
        ],
        "mixamorigRightHand": [
          -9,
          0.4,
          -2
        ],
        "mixamorigLeftUpLeg": [
          -13.1,
          -0.1,
          0.7
        ],
        "mixamorigLeftLeg": [
          -0.3,
          0.1,
          -3.3
        ],
        "mixamorigLeftFoot": [
          -51.2,
          -1.1,
          -2.1
        ],
        "mixamorigRightUpLeg": [
          -13.5,
          0.1,
          -0.7
        ],
        "mixamorigRightLeg": [
          0.2,
          0,
          3.3
        ],
        "mixamorigRightFoot": [
          -50.5,
          1.2,
          2.2
        ]
      },
      "rootOffset": [
        -0.001,
        -0.818,
        0.2
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "bow",
    english: "Bow Pose",
    sanskrit: "Dhanurasana",
    difficulty: "Intermediate",
    categories: ["Prone","Backbends"],
    alignmentCues: ["Hold ankles","Kick into hands","Lift thighs and chest"],
    commonMistakes: ["Only lifting chest","Neck cranking"],
    precautions: ["Shoulder / knee issues: modify or skip"],
    muscles: [m('erectorSpinae', 'working'), m('glutes', 'working'), m('quads', 'lengthening'), m('abs', 'lengthening'), m('biceps', 'working')],
    evidence: defaultEvidence(
      "Prone bow with hip and spinal extension.",
      "Strong posterior chain; anterior chain lengthens.",
      [],
    ),
    keyframes: {
      "pose": {
        "mixamorigHips": [
          81.5,
          0,
          0
        ],
        "mixamorigSpine": [
          -12,
          0,
          0
        ],
        "mixamorigSpine1": [
          -15,
          0,
          0
        ],
        "mixamorigSpine2": [
          -15,
          0,
          0
        ],
        "mixamorigNeck": [
          -8,
          0,
          0
        ],
        "mixamorigLeftArm": [
          35.6,
          5.9,
          -63.3
        ],
        "mixamorigLeftForeArm": [
          8.7,
          1.5,
          -11.7
        ],
        "mixamorigLeftHand": [
          -9,
          -0.3,
          2.9
        ],
        "mixamorigRightArm": [
          35.6,
          -5.9,
          63.2
        ],
        "mixamorigRightForeArm": [
          8.7,
          -1.5,
          11.8
        ],
        "mixamorigRightHand": [
          -9,
          0.4,
          -2
        ],
        "mixamorigLeftUpLeg": [
          -29.8,
          3.2,
          3.9
        ],
        "mixamorigLeftLeg": [
          -99.1,
          2.7,
          0.5
        ],
        "mixamorigLeftFoot": [
          -78.9,
          4.5,
          0.6
        ],
        "mixamorigRightUpLeg": [
          -30.2,
          -3.2,
          -3.9
        ],
        "mixamorigRightLeg": [
          -98.6,
          -2.7,
          -0.5
        ],
        "mixamorigRightFoot": [
          -78.2,
          -4.5,
          -0.6
        ]
      },
      "rootOffset": [
        0,
        -0.825,
        -0.026
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "downward-dog",
    english: "Downward-Facing Dog",
    sanskrit: "Adho Mukha Svanasana",
    difficulty: "Beginner",
    categories: ["Standing","Forward bends","Inversions"],
    alignmentCues: ["Hands shoulder-width","Feet hip-width","Long spine","Heels toward mat (need not touch)","Soft knees OK"],
    commonMistakes: ["Rounded upper back","Shoulders at ears","Collapsing wrists"],
    precautions: ["Wrist pain: fists/forearm variation; glaucoma: avoid long holds"],
    muscles: [m('deltoids', 'working'), m('triceps', 'working'), m('hamstrings', 'lengthening'), m('calves', 'lengthening'), m('abs', 'stabilizer'), m('serratus', 'stabilizer')],
    evidence: defaultEvidence(
      "Inverted V: hips flexed, shoulders flexed, knees may bend.",
      "Shoulder girdle load-bearing; posterior chain stretch.",
      [{ text: "Weight-bearing yoga can load wrists significantly — modify as needed.", url: "https://pubmed.ncbi.nlm.nih.gov/25419403/" }],
    ),
    keyframes: {
      "pose": {
        "mixamorigHips": [
          134.6,
          0,
          0
        ],
        "mixamorigNeck": [
          4,
          0,
          0
        ],
        "mixamorigLeftArm": [
          48.4,
          0.1,
          177.8
        ],
        "mixamorigLeftForeArm": [
          63.9,
          77.2,
          -55.6
        ],
        "mixamorigLeftHand": [
          -49.6,
          -1.1,
          11.9
        ],
        "mixamorigRightArm": [
          48.4,
          -0.1,
          -177.9
        ],
        "mixamorigRightForeArm": [
          64.2,
          -77.1,
          55.9
        ],
        "mixamorigRightHand": [
          -49.6,
          0.6,
          -11.2
        ],
        "mixamorigLeftUpLeg": [
          97,
          -2.1,
          3.1
        ],
        "mixamorigLeftLeg": [
          0.8,
          0,
          -3.9
        ],
        "mixamorigLeftFoot": [
          36.8,
          -1.6,
          1.7
        ],
        "mixamorigRightUpLeg": [
          96.6,
          2.1,
          -3.1
        ],
        "mixamorigRightLeg": [
          1.3,
          0,
          3.9
        ],
        "mixamorigRightFoot": [
          37.4,
          1.7,
          -1.7
        ]
      },
      "rootOffset": [
        0.001,
        -0.239,
        -0.079
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "plank",
    english: "Plank Pose",
    sanskrit: "Phalakasana",
    difficulty: "Beginner",
    categories: ["Prone"],
    alignmentCues: ["Shoulders over wrists","Body in one line","Push floor away","Legs strong"],
    commonMistakes: ["Hips sagging or piking","Head dropping"],
    precautions: ["Wrist issues: forearms"],
    muscles: [m('abs', 'working'), m('transverseAb', 'working'), m('deltoids', 'working'), m('quads', 'stabilizer'), m('glutes', 'stabilizer'), m('serratus', 'stabilizer')],
    evidence: defaultEvidence(
      "Prone straight-body support on hands/toes.",
      "Anti-extension core demand with scapular stabilizers.",
      [],
    ),
    keyframes: {
      "pose": {
        "mixamorigHips": [
          72.5,
          0,
          0
        ],
        "mixamorigLeftArm": [
          39.1,
          -1.7,
          70.7
        ],
        "mixamorigLeftForeArm": [
          -82.3,
          -81.1,
          -89.6
        ],
        "mixamorigLeftHand": [
          -101.9,
          -0.6,
          179.6
        ],
        "mixamorigRightArm": [
          39.1,
          1.7,
          -70.8
        ],
        "mixamorigRightForeArm": [
          -82.4,
          81,
          89.6
        ],
        "mixamorigRightHand": [
          -101.9,
          -0.3,
          -179.6
        ],
        "mixamorigLeftUpLeg": [
          -0.7,
          0,
          -0.4
        ],
        "mixamorigLeftLeg": [
          0.8,
          0,
          -2.7
        ],
        "mixamorigRightUpLeg": [
          -1.1,
          -0.1,
          0.4
        ],
        "mixamorigRightLeg": [
          1.3,
          0,
          2.7
        ]
      },
      "rootOffset": [
        0.001,
        -0.494,
        0.138
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "upward-dog",
    english: "Upward-Facing Dog",
    sanskrit: "Urdhva Mukha Svanasana",
    difficulty: "Intermediate",
    categories: ["Prone","Backbends"],
    alignmentCues: ["Thighs lift","Shoulders over wrists","Chest through arms","Tops of feet press"],
    commonMistakes: ["Shoulders collapsing","Low back pinch"],
    precautions: ["Wrist/shoulder injuries: use cobra instead"],
    muscles: [m('erectorSpinae', 'working'), m('triceps', 'working'), m('glutes', 'stabilizer'), m('abs', 'lengthening'), m('hipFlexors', 'lengthening')],
    evidence: defaultEvidence(
      "Full arm-supported backbend with hips off mat.",
      "Spinal extensors + arm push; anterior chain lengthens.",
      [],
    ),
    keyframes: {
      "pose": {
        "mixamorigHips": [
          69.6,
          0,
          0
        ],
        "mixamorigSpine": [
          -18,
          0,
          0
        ],
        "mixamorigSpine1": [
          -18,
          0,
          0
        ],
        "mixamorigSpine2": [
          -14,
          0,
          0
        ],
        "mixamorigNeck": [
          -10,
          0,
          0
        ],
        "mixamorigHead": [
          -8,
          0,
          0
        ],
        "mixamorigLeftArm": [
          35.5,
          -0.9,
          17.8
        ],
        "mixamorigLeftForeArm": [
          -82.3,
          -81.1,
          -89.6
        ],
        "mixamorigLeftHand": [
          -101.9,
          1.7,
          179.8
        ],
        "mixamorigRightArm": [
          35.5,
          0.9,
          -17.9
        ],
        "mixamorigRightForeArm": [
          -82.4,
          81,
          89.6
        ],
        "mixamorigRightHand": [
          -101.9,
          -2.6,
          -179.7
        ],
        "mixamorigLeftUpLeg": [
          -7.2,
          0.3,
          0.2
        ],
        "mixamorigLeftLeg": [
          0.8,
          0,
          -3.3
        ],
        "mixamorigLeftFoot": [
          -49.6,
          -1.8,
          -1.5
        ],
        "mixamorigRightUpLeg": [
          -7.7,
          -0.3,
          -0.2
        ],
        "mixamorigRightLeg": [
          1.3,
          0,
          3.3
        ],
        "mixamorigRightFoot": [
          -48.9,
          1.8,
          1.6
        ]
      },
      "rootOffset": [
        0.001,
        -0.63,
        0.315
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "child",
    english: "Child's Pose",
    sanskrit: "Balasana",
    difficulty: "Beginner",
    categories: ["Seated","Forward bends"],
    alignmentCues: ["Hips toward heels","Arms forward or by sides","Forehead rests","Wide-knee variation OK"],
    commonMistakes: ["Forcing hips to heels"],
    precautions: ["Knee issues: support with blankets between calves/thighs"],
    muscles: [m('glutes', 'lengthening'), m('erectorSpinae', 'lengthening'), m('quads', 'lengthening'), m('deltoids', 'lengthening')],
    evidence: defaultEvidence(
      "Kneeling flexion with spinal flexion.",
      "Restorative position; tissues unload.",
      [],
    ),
    keyframes: {
      "pose": {
        "mixamorigHips": [
          72.5,
          0,
          0
        ],
        "mixamorigSpine": [
          12,
          0,
          0
        ],
        "mixamorigSpine1": [
          12,
          0,
          0
        ],
        "mixamorigSpine2": [
          8,
          0,
          0
        ],
        "mixamorigHead": [
          12.4,
          0,
          0
        ],
        "mixamorigLeftArm": [
          47.6,
          2.2,
          -172
        ],
        "mixamorigLeftForeArm": [
          98.7,
          82.7,
          -89.2
        ],
        "mixamorigLeftHand": [
          -9,
          -0.3,
          2.9
        ],
        "mixamorigRightArm": [
          47.6,
          -2.3,
          171.9
        ],
        "mixamorigRightForeArm": [
          98.6,
          -82.6,
          89.1
        ],
        "mixamorigRightHand": [
          -9,
          0.4,
          -2
        ],
        "mixamorigLeftUpLeg": [
          134.6,
          13.1,
          8.7
        ],
        "mixamorigLeftLeg": [
          -149.7,
          1.3,
          2.4
        ],
        "mixamorigLeftFoot": [
          -50.9,
          14.5,
          -2.5
        ],
        "mixamorigRightUpLeg": [
          134.2,
          -13.1,
          -8.6
        ],
        "mixamorigRightLeg": [
          -149.3,
          -1.4,
          -2.4
        ],
        "mixamorigRightFoot": [
          -50.2,
          -14.4,
          2.5
        ]
      },
      "rootOffset": [
        0.001,
        -0.665,
        -0.343
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "cat-cow",
    english: "Cat–Cow",
    sanskrit: "Marjaryasana–Bitilasana",
    difficulty: "Beginner",
    categories: ["Prone","Backbends","Forward bends"],
    alignmentCues: ["Hands under shoulders","Knees under hips","Move with breath","Long neck"],
    commonMistakes: ["Dumping into shoulders","Only moving neck"],
    precautions: ["Wrist pain: fists or forearms"],
    muscles: [m('erectorSpinae', 'working'), m('abs', 'working'), m('traps', 'stabilizer'), m('serratus', 'stabilizer')],
    evidence: defaultEvidence(
      "Alternating spinal flexion and extension on all fours.",
      "Segmental mobility drill; activation alternates flexors/extensors.",
      [],
    ),
    keyframes: {
      "pose": {
        "mixamorigHips": [
          90,
          0,
          0
        ],
        "mixamorigSpine": [
          -10,
          0,
          0
        ],
        "mixamorigSpine1": [
          -10,
          0,
          0
        ],
        "mixamorigSpine2": [
          -8,
          0,
          0
        ],
        "mixamorigNeck": [
          -18,
          0,
          0
        ],
        "mixamorigHead": [
          -12,
          0,
          0
        ],
        "mixamorigLeftArm": [
          39,
          -1,
          60.2
        ],
        "mixamorigLeftForeArm": [
          -82.3,
          -81.1,
          -89.6
        ],
        "mixamorigLeftHand": [
          -101.9,
          -1.1,
          179.6
        ],
        "mixamorigRightArm": [
          39,
          1,
          -60.3
        ],
        "mixamorigRightForeArm": [
          -82.4,
          81,
          89.6
        ],
        "mixamorigRightHand": [
          -101.9,
          0.3,
          -179.6
        ],
        "mixamorigLeftUpLeg": [
          89.3,
          -1.1,
          1.7
        ],
        "mixamorigLeftLeg": [
          -86.3,
          2.7,
          -0.1
        ],
        "mixamorigLeftFoot": [
          -52.2,
          0,
          -1.2
        ],
        "mixamorigRightUpLeg": [
          88.8,
          1.1,
          -1.7
        ],
        "mixamorigRightLeg": [
          -85.8,
          -2.7,
          0.1
        ],
        "mixamorigRightFoot": [
          -51.5,
          0.1,
          1.2
        ]
      },
      "hold": {
        "mixamorigHips": [
          90,
          0,
          0
        ],
        "mixamorigSpine": [
          12,
          0,
          0
        ],
        "mixamorigSpine1": [
          12,
          0,
          0
        ],
        "mixamorigSpine2": [
          10,
          0,
          0
        ],
        "mixamorigNeck": [
          20,
          0,
          0
        ],
        "mixamorigHead": [
          12,
          0,
          0
        ],
        "mixamorigLeftArm": [
          40.7,
          -1,
          122.2
        ],
        "mixamorigLeftForeArm": [
          -82.3,
          -81.1,
          -89.6
        ],
        "mixamorigLeftHand": [
          -101.9,
          -1.1,
          179.6
        ],
        "mixamorigRightArm": [
          40.7,
          1,
          -122.3
        ],
        "mixamorigRightForeArm": [
          -82.4,
          81,
          89.6
        ],
        "mixamorigRightHand": [
          -101.9,
          0.3,
          -179.6
        ],
        "mixamorigLeftUpLeg": [
          89.3,
          -1.1,
          1.7
        ],
        "mixamorigLeftLeg": [
          -86.3,
          2.7,
          -0.1
        ],
        "mixamorigLeftFoot": [
          -52.2,
          0,
          -1.2
        ],
        "mixamorigRightUpLeg": [
          88.8,
          1.1,
          -1.7
        ],
        "mixamorigRightLeg": [
          -85.8,
          -2.7,
          0.1
        ],
        "mixamorigRightFoot": [
          -51.5,
          0.1,
          1.2
        ]
      },
      "rootOffset": [
        0.001,
        -0.375,
        0.022
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "pigeon",
    english: "Pigeon Pose",
    sanskrit: "Eka Pada Rajakapotasana (prep)",
    difficulty: "Intermediate",
    categories: ["Seated","Forward bends"],
    alignmentCues: ["Front shin as comfortable","Square hips as able","Support with props","Fold forward optionally"],
    commonMistakes: ["Forcing front knee angle","Rolling onto outer hip"],
    precautions: ["Knee ligament issues: use reclined figure-4 instead"],
    muscles: [m('piriformis', 'lengthening'), m('glutes', 'lengthening'), m('hipFlexors', 'lengthening'), m('abs', 'stabilizer')],
    evidence: defaultEvidence(
      "Asymmetric hip flexion/external rotation front; extension rear.",
      "Targets deep external rotators; intensity ≠ medical “hip opening detox”.",
      [],
    ),
    keyframes: {
      "pose": {
        "mixamorigSpine": [
          -4,
          0,
          0
        ],
        "mixamorigLeftArm": [
          25.8,
          1.9,
          0
        ],
        "mixamorigLeftForeArm": [
          -84.6,
          -82.2,
          -94.5
        ],
        "mixamorigLeftHand": [
          -108.5,
          8.5,
          179.8
        ],
        "mixamorigRightArm": [
          25.8,
          -1.9,
          -0.2
        ],
        "mixamorigRightForeArm": [
          -84.6,
          82.1,
          94.6
        ],
        "mixamorigRightHand": [
          -108.5,
          -9.4,
          -179.8
        ],
        "mixamorigLeftUpLeg": [
          125.5,
          -65.4,
          55
        ],
        "mixamorigLeftLeg": [
          -137.2,
          1.8,
          2
        ],
        "mixamorigLeftFoot": [
          46.8,
          85.6,
          -53.2
        ],
        "mixamorigRightUpLeg": [
          -80.2,
          -2.1,
          -1.4
        ],
        "mixamorigRightLeg": [
          1.3,
          0,
          2.7
        ],
        "mixamorigRightFoot": [
          -52.4,
          1.7,
          1.7
        ]
      },
      "rootOffset": [
        0.001,
        -0.654,
        0.316
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "camel",
    english: "Camel Pose",
    sanskrit: "Ustrasana",
    difficulty: "Intermediate",
    categories: ["Seated","Backbends"],
    alignmentCues: ["Knees hip-width","Lift chest first","Hands to heels as able","Hips forward"],
    commonMistakes: ["Dumping into lumbar","Head hanging too soon"],
    precautions: ["Neck issues: keep chin slightly tucked / look forward"],
    muscles: [m('erectorSpinae', 'working'), m('glutes', 'working'), m('quads', 'lengthening'), m('hipFlexors', 'lengthening'), m('abs', 'lengthening'), m('pecs', 'lengthening')],
    evidence: defaultEvidence(
      "Kneeling backbend.",
      "Spinal and hip extensors work; anterior chain lengthens.",
      [],
    ),
    keyframes: {
      "pose": {
        "mixamorigHips": [
          -12,
          0,
          0
        ],
        "mixamorigSpine": [
          -22,
          0,
          0
        ],
        "mixamorigSpine1": [
          -24,
          0,
          0
        ],
        "mixamorigSpine2": [
          -22,
          0,
          0
        ],
        "mixamorigNeck": [
          -20,
          0,
          0
        ],
        "mixamorigHead": [
          -18,
          0,
          0
        ],
        "mixamorigLeftArm": [
          -142.9,
          -18.7,
          -47.7
        ],
        "mixamorigLeftForeArm": [
          -176.6,
          -14.1,
          -160.9
        ],
        "mixamorigLeftHand": [
          -9,
          -0.3,
          2.9
        ],
        "mixamorigRightArm": [
          -143,
          18.7,
          47.8
        ],
        "mixamorigRightForeArm": [
          -176.6,
          14.1,
          160.8
        ],
        "mixamorigRightHand": [
          -9,
          0.4,
          -2
        ],
        "mixamorigLeftUpLeg": [
          -27.1,
          1.6,
          -0.2
        ],
        "mixamorigLeftLeg": [
          -72.4,
          2.6,
          -0.8
        ],
        "mixamorigLeftFoot": [
          -51.6,
          -0.2,
          -1.1
        ],
        "mixamorigRightUpLeg": [
          -27.6,
          -1.7,
          0.1
        ],
        "mixamorigRightLeg": [
          -72,
          -2.6,
          0.8
        ],
        "mixamorigRightFoot": [
          -50.9,
          0.3,
          1.2
        ]
      },
      "rootOffset": [
        0,
        -0.415,
        0.321
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "boat",
    english: "Boat Pose",
    sanskrit: "Navasana",
    difficulty: "Intermediate",
    categories: ["Seated"],
    alignmentCues: ["Lift chest","Shin parallel or knees bent","Balance on sit bones","Long spine"],
    commonMistakes: ["Rounding low back","Holding breath"],
    precautions: ["Pregnancy / disc issues: bent-knee variation or skip"],
    muscles: [m('abs', 'working'), m('hipFlexors', 'working'), m('quads', 'stabilizer'), m('erectorSpinae', 'stabilizer')],
    evidence: defaultEvidence(
      "Seated V shape balancing on pelvis.",
      "High hip flexor and anterior core demand.",
      [],
    ),
    keyframes: {
      "pose": {
        "mixamorigHips": [
          -41.4,
          0,
          0
        ],
        "mixamorigLeftArm": [
          35.9,
          -3.7,
          46.8
        ],
        "mixamorigLeftForeArm": [
          8.4,
          -0.1,
          -8.9
        ],
        "mixamorigLeftHand": [
          -9,
          -0.3,
          2.9
        ],
        "mixamorigRightArm": [
          35.9,
          3.7,
          -46.9
        ],
        "mixamorigRightForeArm": [
          8.4,
          0.1,
          9
        ],
        "mixamorigRightHand": [
          -9,
          0.4,
          -2
        ],
        "mixamorigLeftUpLeg": [
          96.6,
          -2.1,
          2.5
        ],
        "mixamorigLeftLeg": [
          0.8,
          0,
          -2.7
        ],
        "mixamorigLeftFoot": [
          -67.9,
          -1.1,
          -1.4
        ],
        "mixamorigRightUpLeg": [
          96.2,
          2.1,
          -2.5
        ],
        "mixamorigRightLeg": [
          1.3,
          0,
          2.7
        ],
        "mixamorigRightFoot": [
          -67.2,
          1.1,
          1.5
        ]
      },
      "rootOffset": [
        0,
        -0.824,
        -0.082
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "half-moon",
    english: "Half Moon Pose",
    sanskrit: "Ardha Chandrasana",
    difficulty: "Intermediate",
    categories: ["Standing","Balance"],
    alignmentCues: ["Stack hips","Standing foot forward","Top hip opens","Gaze up or down"],
    commonMistakes: ["Hips rotating down","Locking standing knee"],
    precautions: ["Use block under bottom hand"],
    muscles: [m('glutes', 'working'), m('abductors', 'working'), m('quads', 'stabilizer'), m('abs', 'stabilizer'), m('deltoids', 'stabilizer')],
    evidence: defaultEvidence(
      "Single-leg balance with torso and free leg roughly horizontal.",
      "Stance hip abductors dominate; ankle strategies assist.",
      [],
    ),
    keyframes: {
      "pose": {
        "mixamorigHips": [
          90,
          -69.6,
          0
        ],
        "mixamorigSpine1": [
          0,
          8,
          0
        ],
        "mixamorigSpine2": [
          0,
          8,
          0
        ],
        "mixamorigNeck": [
          0,
          -20,
          0
        ],
        "mixamorigHead": [
          0,
          -30,
          0
        ],
        "mixamorigLeftArm": [
          -53.5,
          -0.1,
          42.6
        ],
        "mixamorigLeftForeArm": [
          -82.3,
          -81.1,
          -89.6
        ],
        "mixamorigLeftHand": [
          -9,
          -0.3,
          2.9
        ],
        "mixamorigRightArm": [
          -49.2,
          -1.5,
          34.8
        ],
        "mixamorigRightForeArm": [
          -82.4,
          81,
          89.6
        ],
        "mixamorigRightHand": [
          -9,
          0.4,
          -2
        ],
        "mixamorigLeftUpLeg": [
          89.3,
          -2.1,
          68.4
        ],
        "mixamorigLeftLeg": [
          0.8,
          0,
          -1.6
        ],
        "mixamorigLeftFoot": [
          -0.1,
          -0.9,
          1.3
        ],
        "mixamorigRightUpLeg": [
          -1.9,
          20.4,
          0
        ],
        "mixamorigRightLeg": [
          1.3,
          0,
          2.7
        ]
      },
      "rootOffset": [
        0.015,
        0.044,
        0.146
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "wide-legged-fold",
    english: "Wide-Legged Forward Fold",
    sanskrit: "Prasarita Padottanasana",
    difficulty: "Beginner",
    categories: ["Standing","Forward bends"],
    alignmentCues: ["Wide stance","Hinge at hips","Hands under shoulders or clasp","Soft knees OK"],
    commonMistakes: ["Rounding to force head down"],
    precautions: ["Hamstring strain history: bend knees"],
    muscles: [m('hamstrings', 'lengthening'), m('adductors', 'lengthening'), m('erectorSpinae', 'lengthening'), m('calves', 'lengthening')],
    evidence: defaultEvidence(
      "Bilateral hip flexion in wide stance.",
      "Posterior and medial thigh lengthening.",
      [],
    ),
    keyframes: {
      "pose": {
        "mixamorigHips": [
          90,
          -5,
          90
        ],
        "mixamorigSpine": [
          14,
          0,
          0
        ],
        "mixamorigSpine1": [
          14,
          0,
          0
        ],
        "mixamorigSpine2": [
          10,
          0,
          0
        ],
        "mixamorigNeck": [
          10,
          0,
          0
        ],
        "mixamorigHead": [
          8,
          0,
          0
        ],
        "mixamorigLeftArm": [
          42.8,
          -4.4,
          124.2
        ],
        "mixamorigLeftForeArm": [
          -82.7,
          -81.1,
          -91.1
        ],
        "mixamorigLeftHand": [
          -107.5,
          1.8,
          179.7
        ],
        "mixamorigRightArm": [
          42.8,
          4.4,
          -124.3
        ],
        "mixamorigRightForeArm": [
          -82.7,
          81,
          91.1
        ],
        "mixamorigRightHand": [
          -107.6,
          -2.6,
          -179.8
        ],
        "mixamorigLeftUpLeg": [
          84.3,
          -2.1,
          31.2
        ],
        "mixamorigLeftLeg": [
          0.8,
          0,
          -2.7
        ],
        "mixamorigLeftFoot": [
          -3.9,
          -26.6,
          -17.5
        ],
        "mixamorigRightUpLeg": [
          83.9,
          2.1,
          -31.2
        ],
        "mixamorigRightLeg": [
          1.3,
          0,
          2.7
        ],
        "mixamorigRightFoot": [
          -3.2,
          26.7,
          17.5
        ]
      },
      "rootOffset": [
        0.218,
        -0.157,
        0.024
      ]
    },
    timing: defaultTiming,
  }),
  pose({
    id: "low-lunge",
    english: "Low Lunge",
    sanskrit: "Anjaneyasana",
    difficulty: "Beginner",
    categories: ["Standing","Backbends"],
    alignmentCues: ["Back knee down","Front knee over ankle","Hips forward gently","Arms up or on front thigh"],
    commonMistakes: ["Front knee collapsing in","Cranking lumbar"],
    precautions: ["Knee padding for back knee"],
    muscles: [m('hipFlexors', 'lengthening'), m('quads', 'working'), m('glutes', 'stabilizer'), m('abs', 'stabilizer'), m('deltoids', 'stabilizer')],
    evidence: defaultEvidence(
      "Kneeling lunge with hip extension rear / flexion front.",
      "Rear hip flexors lengthen; front quads stabilize.",
      [],
    ),
    keyframes: {
      "pose": {
        "mixamorigHips": [
          -8,
          0,
          0
        ],
        "mixamorigSpine": [
          -6,
          0,
          0
        ],
        "mixamorigSpine1": [
          -7,
          0,
          0
        ],
        "mixamorigSpine2": [
          -7,
          0,
          0
        ],
        "mixamorigNeck": [
          -8,
          0,
          0
        ],
        "mixamorigHead": [
          -8,
          0,
          0
        ],
        "mixamorigLeftShoulder": [
          -31.1,
          -2.9,
          10.3
        ],
        "mixamorigLeftArm": [
          78.6,
          -13,
          156.1
        ],
        "mixamorigLeftForeArm": [
          8.3,
          -0.7,
          -8.9
        ],
        "mixamorigLeftHand": [
          -9,
          -0.3,
          2.9
        ],
        "mixamorigRightShoulder": [
          -31.1,
          2.9,
          -10.4
        ],
        "mixamorigRightArm": [
          78.6,
          13,
          -156.2
        ],
        "mixamorigRightForeArm": [
          8.3,
          0.7,
          9
        ],
        "mixamorigRightHand": [
          -9,
          0.4,
          -2
        ],
        "mixamorigLeftUpLeg": [
          68.5,
          -2.7,
          1.5
        ],
        "mixamorigLeftLeg": [
          -71.8,
          2.6,
          -0.8
        ],
        "mixamorigLeftFoot": [
          -4.7,
          0.5,
          -0.7
        ],
        "mixamorigRightUpLeg": [
          -54.5,
          -1.5,
          -0.5
        ],
        "mixamorigRightLeg": [
          -40.4,
          -1.8,
          2
        ],
        "mixamorigRightFoot": [
          -49.8,
          0.5,
          1.1
        ]
      },
      "rootOffset": [
        0,
        -0.314,
        0.154
      ]
    },
    timing: defaultTiming,
  }),
]

export const POSE_COUNT = POSES.length

export function getPoseById(id: string): YogaPose | undefined {
  return POSES.find((p) => p.id === id)
}

export const ALL_CATEGORIES = [
  'Standing','Seated','Supine','Prone','Balance','Twists','Forward bends','Backbends','Inversions',
] as const
